import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import BlogForm from '../../components/BlogForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = { title: '', content: '', featured_image: '' };

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchBlogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/blogs`);
      setBlogs(data);
    } catch (error) {
      console.error('Blogs fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingBlog) {
        await axios.put(`${API_URL}/api/blogs/${editingBlog.id}`, formData, { withCredentials: true });
        setSuccess('Blog updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/blogs`, formData, { withCredentials: true });
        setSuccess('Blog created successfully!');
      }
      fetchBlogs();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Operation failed');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({ title: blog.title, content: blog.content, featured_image: blog.featured_image || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await axios.delete(`${API_URL}/api/blogs/${id}`, { withCredentials: true });
      setSuccess('Blog deleted successfully!');
      fetchBlogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Delete failed');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingBlog(null);
    setShowModal(false);
    setError('');
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2" data-testid="blogs-title">Blog & News Management</h1>
          <p className="text-text_muted">Create and manage your blog posts</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all" data-testid="add-blog-button">
          <Plus size={20} />
          <span>Add Blog Post</span>
        </button>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700" data-testid="success-message">{success}</div>
      )}

      {loading ? (
        <div className="text-center py-20" data-testid="loading">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text_muted">Loading blogs...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog, index) => (
            <div key={blog.id} className="bg-white rounded-sm border border-border overflow-hidden" data-testid={`blog-card-${index}`}>
              {blog.featured_image && (
                <div className="h-48 overflow-hidden">
                  <img src={getImageSrc(blog.featured_image)} alt={blog.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6">
                <p className="text-sm text-text_muted mb-2">{formatDate(blog.created_at)}</p>
                <h3 className="text-xl font-semibold text-primary mb-3 line-clamp-2">{blog.title}</h3>
                <p className="text-text_muted leading-relaxed line-clamp-3 mb-4">{stripHtml(blog.content)}</p>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(blog)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors" data-testid={`edit-blog-${index}`}>
                    <Edit size={16} /><span>Edit</span>
                  </button>
                  <button onClick={() => handleDelete(blog.id)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors" data-testid={`delete-blog-${index}`}>
                    <Trash2 size={16} /><span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <BlogForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={resetForm}
          isEditing={!!editingBlog}
          error={error}
        />
      )}
    </div>
  );
};

export default AdminBlogs;
